import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DateField } from "./DateField";
import { useNavigate } from "react-router-dom";
import {
  apiError,
  getOperationalAreas,
  getPreAnalysis,
  getProcesses,
  getSites,
  startAnalysis,
  updateAnalysisMetadata,
} from "../../services/AiService";
import { LovItem, PreAnalysisView, Site } from "../../types/ai";
import { AnalysisStatusChip, ErrorAlert, Loading, SecureImage, fmtDate } from "./Shared";

interface Props {
  checklistId: number | null;
  onClose: () => void;
}

interface ContextForm {
  siteId: string;
  operationalArea: string;
  process: string;
  temperatureMin: string;
  temperatureMax: string;
  awb: string;
  flightNo: string;
  flightDate: string;
  origin: string;
  destination: string;
  specialHandlingCodes: string;
  cargoDescription: string;
}

const toForm = (v: PreAnalysisView): ContextForm => ({
  siteId: v.siteId ? String(v.siteId) : "",
  operationalArea: v.checklist.operationalArea || "",
  process: v.checklist.process || "",
  temperatureMin: v.temperatureMin != null ? String(v.temperatureMin) : "",
  temperatureMax: v.temperatureMax != null ? String(v.temperatureMax) : "",
  // An AWB read from the checklist text is only a suggestion; saving must not turn it into a recorded value.
  awb: v.checklist.awbFromChecklistText ? "" : v.checklist.shipment?.awb || "",
  flightNo: v.checklist.shipment?.flightNo || "",
  flightDate: v.checklist.shipment?.flightDate || "",
  origin: v.checklist.shipment?.origin || "",
  destination: v.checklist.shipment?.destination || "",
  specialHandlingCodes: v.checklist.shipment?.specialHandlingCodes || "",
  cargoDescription: v.checklist.shipment?.cargoDescription || "",
});

/** Language the narrative is written in: the user's app language (the web app calls Spanish "sp"). */
const appLanguage = (lng?: string) => (lng || "en").slice(0, 2);

/**
 * Step before "Analyse with QualiCheck AI": shows what QualiCheck extracted from the mobile submission (read-only, with
 * confidence) and lets an authorised user complete the analysis context. Existing values need a reason to change.
 */
const AiPreAnalysis: React.FC<Props> = ({ checklistId, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState<PreAnalysisView | null>(null);
  const [form, setForm] = useState<ContextForm | null>(null);
  const [initial, setInitial] = useState<ContextForm | null>(null);
  const [reason, setReason] = useState("");
  const [sites, setSites] = useState<Site[]>([]);
  const [processes, setProcesses] = useState<LovItem[]>([]);
  const [areas, setAreas] = useState<LovItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!checklistId) return;
    setView(null);
    setError(null);
    setReason("");
    getPreAnalysis(checklistId)
      .then((v) => {
        setView(v);
        setForm(toForm(v));
        setInitial(toForm(v));
      })
      .catch((e) => setError(apiError(e)));
    getSites().then(setSites).catch(() => setSites([]));
    getProcesses().then(setProcesses).catch(() => setProcesses([]));
    getOperationalAreas().then(setAreas).catch(() => setAreas([]));
  }, [checklistId]);

  const dirty = useMemo(() => !!form && !!initial && JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const overwrites = useMemo(
    () => !!form && !!initial && (Object.keys(form) as (keyof ContextForm)[]).some((k) => initial[k] !== "" && initial[k] !== form[k]),
    [form, initial]
  );
  const evidenceById = useMemo(() => new Map((view?.evidence || []).map((e) => [e.id, e])), [view]);
  const latest = view?.analyses?.[0];

  const set = (k: keyof ContextForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => (f ? { ...f, [k]: e.target.value } : f));
  const missing = (key: string) => view?.missingContext.includes(key) && <span className="aces-missing"> · {t("aces.pre.missing")}</span>;

  const saveContext = async () => {
    if (!form || !checklistId) return;
    const num = (v: string) => (v.trim() === "" ? null : Number(v));
    const shipment = {
      awb: form.awb || null, flightNo: form.flightNo || null, flightDate: form.flightDate || null,
      origin: form.origin || null, destination: form.destination || null,
      specialHandlingCodes: form.specialHandlingCodes || null, cargoDescription: form.cargoDescription || null,
    };
    await updateAnalysisMetadata(checklistId, {
      siteId: form.siteId ? Number(form.siteId) : null,
      operationalArea: form.operationalArea || null,
      process: form.process || null,
      temperatureMin: num(form.temperatureMin),
      temperatureMax: num(form.temperatureMax),
      shipment: Object.values(shipment).some((v) => v) ? shipment : null,
      reason: reason || undefined,
    });
  };

  const run = async () => {
    if (!checklistId) return;
    setBusy(true);
    setError(null);
    try {
      if (dirty) await saveContext();
      const analysis = await startAnalysis(checklistId, appLanguage(i18n.language));
      onClose();
      navigate(`/ai/analysis/${analysis.id}`);
    } catch (e) {
      setError(apiError(e, t("aces.analysis.unavailable")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={!!checklistId} onHide={onClose} size="xl" centered className="__modal" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="ri-sparkling-2-line"></i> {t("aces.analyseButton")}
          {view && <span className="aces-muted" style={{ marginLeft: 10 }}>{view.checklist.referenceNo} — {view.checklist.title}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorAlert message={error} onClose={() => setError(null)} />
        {!view || !form ? (
          !error && <Loading text={t("aces.pre.loading")} />
        ) : (
          <div className="aces-compare">
            <div>
              <div className="col-title">{t("aces.pre.submission")}</div>
              <dl className="aces-kv">
                <dt>{t("aces.pre.submittedBy")}</dt>
                <dd>{view.submittedBy?.name || "—"} · {fmtDate(view.submittedAt)}</dd>
                <dt>{t("aces.pre.source")}</dt>
                <dd>{view.source ? t(`aces.enum.source.${view.source}`) : "—"}</dd>
                <dt>{t("aces.pre.temperature")}</dt>
                <dd>{view.temperature != null ? `${view.temperature} °C` : "—"}</dd>
                <dt>{t("aces.pre.comment")}</dt>
                <dd>{view.overallComment || "—"}</dd>
                <dt>{t("aces.pre.signature")}</dt>
                <dd>{view.signaturePresent ? t("aces.common.yes") : t("aces.common.no")}</dd>
              </dl>
              {view.parseStatus === "NEEDS_VISION" && <div className="aces-draft-note mt-2">{t("aces.pre.needsVision")}</div>}
              {view.parseStatus === "FAILED" && <div className="aces-error mt-2">{t("aces.pre.unreadable")}</div>}
              {view.parseWarnings.length > 0 && (
                <div className="aces-draft-note mt-2">
                  <b>{t("aces.pre.warnings")}</b>
                  <ul style={{ margin: 0 }}>{view.parseWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
              {view.pdfUrl && (
                <p className="mt-2">
                  <a href={view.pdfUrl} target="_blank" rel="noopener noreferrer"><i className="ri-file-pdf-line"></i> {t("aces.pre.viewPdf")}</a>
                </p>
              )}
              <div className="__defaultTable aces-stack mt-2">
                <table>
                  <thead>
                    <tr>
                      <th>{t("aces.pre.question")}</th>
                      <th>{t("aces.pre.answer")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.answers.map((a) => (
                      <tr key={a.questionId}>
                        <td>
                          <div>{a.title}</div>
                          {a.requirementText && <div className="aces-muted">{a.requirementText}</div>}
                          {a.evidenceIds.length > 0 && (
                            <div className="aces-photos mt-1">
                              {a.evidenceIds.map((id) => (
                                <SecureImage key={id} fileId={id} alt={evidenceById.get(id)?.code || ""} />
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <b>{a.answer || "—"}</b>
                          {a.section === "MANAGER" && <div className="aces-muted">{t("aces.pre.manager")}</div>}
                          {a.reason && <div className="aces-muted">{a.reason}</div>}
                          {a.confidence && a.confidence !== "HIGH" && <div className="aces-confidence-low">{t(`aces.enum.confidence.${a.confidence}`)}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="col-title">{t("aces.pre.context")}</div>
              <div className="aces-field">
                <label htmlFor="pre-siteId">{t("aces.ctx.site")}{missing("site")}</label>
                <select className="form-control" id="pre-siteId" value={form.siteId} onChange={set("siteId")}>
                  <option value="">—</option>
                  {sites.filter((s) => s.active || String(s.id) === form.siteId).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}{s.airportIata ? ` (${s.airportIata})` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="aces-grid-2">
                <div className="aces-field">
                  <label htmlFor="pre-operationalArea">{t("aces.ctx.operationalArea")}{missing("operationalArea")}</label>
                  <input className="form-control" list="aces-areas" id="pre-operationalArea" value={form.operationalArea} onChange={set("operationalArea")} />
                  <datalist id="aces-areas">{areas.map((a) => <option key={a.value} value={a.value} />)}</datalist>
                </div>
                <div className="aces-field">
                  <label htmlFor="pre-process">{t("aces.ctx.process")}{missing("process")}</label>
                  <input className="form-control" list="aces-processes" id="pre-process" value={form.process} onChange={set("process")} />
                  <datalist id="aces-processes">{processes.map((p) => <option key={p.value} value={p.value} />)}</datalist>
                </div>
                <div className="aces-field">
                  <label htmlFor="pre-temperatureMin">{t("aces.ctx.temperatureMin")}{missing("temperatureRange")}</label>
                  <input className="form-control" type="number" step="0.1" id="pre-temperatureMin" value={form.temperatureMin} onChange={set("temperatureMin")} />
                </div>
                <div className="aces-field">
                  <label htmlFor="pre-temperatureMax">{t("aces.ctx.temperatureMax")}</label>
                  <input className="form-control" type="number" step="0.1" id="pre-temperatureMax" value={form.temperatureMax} onChange={set("temperatureMax")} />
                </div>
              </div>
              <h6 className="mt-2">{t("aces.ctx.shipment")}</h6>
              <div className="aces-grid-2">
                <div className="aces-field"><label htmlFor="pre-awb">{t("aces.ctx.awb")}{missing("shipment.awb")}</label><input className="form-control" id="pre-awb" placeholder={view?.checklist.awbFromChecklistText ? `${view.checklist.shipment?.awb} (${t("aces.analysis.awbFromText")})` : undefined} value={form.awb} onChange={set("awb")} /></div>
                <div className="aces-field"><label htmlFor="pre-flightNo">{t("aces.ctx.flightNo")}{missing("shipment.flightNo")}</label><input className="form-control" id="pre-flightNo" value={form.flightNo} onChange={set("flightNo")} /></div>
                <div className="aces-field"><label htmlFor="pre-flightDate">{t("aces.ctx.flightDate")}</label><DateField id="pre-flightDate" value={form.flightDate} onChange={(v) => setForm((f) => (f ? { ...f, flightDate: v } : f))} /></div>
                <div className="aces-field"><label htmlFor="pre-specialHandlingCodes">{t("aces.ctx.shc")}</label><input className="form-control" id="pre-specialHandlingCodes" value={form.specialHandlingCodes} onChange={set("specialHandlingCodes")} placeholder="PIL, CRT" /></div>
                <div className="aces-field"><label htmlFor="pre-origin">{t("aces.ctx.origin")}</label><input className="form-control" maxLength={4} id="pre-origin" value={form.origin} onChange={set("origin")} placeholder="IATA" /></div>
                <div className="aces-field"><label htmlFor="pre-destination">{t("aces.ctx.destination")}</label><input className="form-control" maxLength={4} id="pre-destination" value={form.destination} onChange={set("destination")} placeholder="IATA" /></div>
              </div>
              <div className="aces-field"><label htmlFor="pre-cargoDescription">{t("aces.ctx.cargo")}</label><input className="form-control" id="pre-cargoDescription" value={form.cargoDescription} onChange={set("cargoDescription")} /></div>
              {overwrites && (
                <div className="aces-field">
                  <label htmlFor="pre-reason">{t("aces.pre.changeReason")} <span style={{ color: "red" }}>*</span></label>
                  <input id="pre-reason" className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
              )}
              {latest && dirty && <div className="aces-draft-note mt-2">{t("aces.pre.contextAppliesNext")}</div>}
              {latest && (
                <div className="aces-card mt-3">
                  <div className="aces-muted">{t("aces.pre.existing")}</div>
                  <div className="aces-actions mt-1">
                    <span>{latest.analysisRef} v{latest.version}</span>
                    <AnalysisStatusChip value={latest.status} />
                    <button className="aces-btn small secondary" onClick={() => { onClose(); navigate(`/ai/analysis/${latest.id}`); }}>
                      {t("aces.common.view")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {view && !view.aiEnabled && <span className="aces-missing" style={{ marginRight: "auto" }}>{t("aces.pre.disabled")}</span>}
        <button className="aces-btn secondary" onClick={onClose}>{t("common.cancel")}</button>
        <button
          className="aces-btn"
          disabled={busy || !view || !view.aiEnabled || view.parseStatus === "FAILED" || (overwrites && !reason.trim())}
          onClick={run}
        >
          {busy ? <i className="ri-loader-4-line ri-spin"></i> : <i className="ri-sparkling-2-line"></i>}
          {latest ? t("aces.pre.openAnalysis") : t("aces.pre.run")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AiPreAnalysis;
